import React, { useState, useEffect, useRef } from 'react';
import { Modal, Spin, Alert, Button, message } from 'antd';
import { CloseOutlined, ReloadOutlined, StopOutlined } from '@ant-design/icons';
import meoPortalService, { MeoPortalStatus, MeoPortalSession } from '../services/meoPortalService';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import { isAdmin } from '../utils/roleUtils';

interface MEOPortalModalProps {
  open: boolean;
  onClose: () => void;
}

const MEOPortalModal: React.FC<MEOPortalModalProps> = ({ open, onClose }) => {
  const { user } = useCognitoAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionActive, setSessionActive] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [activeSessions, setActiveSessions] = useState<MeoPortalSession[]>([]);

  const isAdminFlag = user && user.role ? isAdmin(user.role) : false;

  useEffect(() => {
    if (open) {
      checkSessionStatus();
      if (isAdminFlag) {
        loadActiveSessions();
      }
    }
  }, [open, isAdmin]);

  const checkSessionStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const status: MeoPortalStatus = await meoPortalService.getStatus();
      setSessionActive(status.active);
      setSessionId(status.session_id || null);
      
      if (!status.active) {
        // Don't automatically try to login - just show the error state
        setError('Nenhuma sessão ativa encontrada. Clique em "Fazer Login" para conectar ao portal MEO.');
      } else {
        // Session is active for current user
        setError(null);
      }
    } catch (error: any) {
      setError('Erro ao verificar o estado da sessão do portal MEO.');
    } finally {
      setLoading(false);
    }
  };

  const loadActiveSessions = async () => {
    try {
      const response = await meoPortalService.getActiveSessions();
      if (response.success) {
        setActiveSessions(response.sessions);
      }
    } catch (error) {
    }
  };

  const loginToPortal = async () => {
    try {
      setLoading(true);
      const response = await meoPortalService.login();
      if (response.success) {
        setSessionActive(true);
        setSessionId(response.session_id || null);
        setError(null);
        
        // Open MEO portal in new window
        const portalUrl = meoPortalService.getPortalUrl();
        window.open(portalUrl, '_blank', 'noopener,noreferrer');
        
        if (isAdminFlag) {
          loadActiveSessions(); // Refresh sessions list
        }
      } else {
        setError(response.message);
      }
    } catch (error: any) {
      setError('Erro ao fazer login no portal MEO.');
    } finally {
      setLoading(false);
    }
  };

  const handleForceTerminate = async (targetUser?: string) => {
    try {
      setLoading(true);
      const response = await meoPortalService.forceTerminateSession(targetUser);
      if (response.success) {
        message.success('Sessão(s) terminada(s) com sucesso.');
        if (targetUser === user?.username) {
          // If terminating own session, try to login again
          setSessionActive(false);
          setSessionId(null);
          await loginToPortal();
        } else {
          // Refresh sessions list
          loadActiveSessions();
        }
      } else {
        setError(response.message);
      }
    } catch (error: any) {
      setError('Erro ao terminar a sessão forçadamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = async () => {
    if (sessionActive) {
      try {
        await meoPortalService.releaseSession();
        message.success('Sessão do portal MEO terminada.');
      } catch (error) {
      }
    }
    setSessionActive(false);
    setSessionId(null);
    setError(null);
    onClose();
  };

  const handleRetry = () => {
    checkSessionStatus();
  };

  const canAccess = user && user.role && ['admin', 'diretor_comercial', 'team_leader'].includes(user.role);

  if (!canAccess) {
    return null;
  }

  return (
    <Modal
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Portal MEO</span>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={handleClose}
            style={{ border: 'none', padding: '4px 8px' }}
          />
        </div>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      width="95vw"
      style={{ top: 20, maxWidth: '95vw' }}
      bodyStyle={{ 
        height: '85vh', 
        padding: 0, 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden'
      }}
      destroyOnClose
    >
      {loading && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          flexDirection: 'column',
          gap: 16
        }}>
          <Spin size="large" />
          <div>A conectar ao portal MEO...</div>
        </div>
      )}

      {error && !loading && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          flexDirection: 'column',
          gap: 16,
          padding: 24
        }}>
          <Alert
            message="Erro de Acesso"
            description={error}
            type="error"
            showIcon
            style={{ maxWidth: 500 }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button 
              type="primary" 
              icon={<ReloadOutlined />} 
              onClick={handleRetry}
            >
              Tentar Novamente
            </Button>
            <Button 
              type="default" 
              onClick={loginToPortal}
              loading={loading}
            >
              Fazer Login
            </Button>
            {isAdminFlag && (
              <Button 
                type="default" 
                danger
                icon={<StopOutlined />} 
                onClick={() => handleForceTerminate()}
                loading={loading}
              >
                Terminar Todas as Sessões
              </Button>
            )}
          </div>
        </div>
      )}

      {sessionActive && !loading && !error && (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: 16,
          padding: 24
        }}>
          <div style={{ textAlign: 'center' }}>
            <h3>✅ Sessão MEO Portal Ativa</h3>
            <p>O portal MEO foi aberto numa nova janela. Pode fechar esta janela.</p>
          </div>
          
          <Button 
            type="primary" 
            size="large"
            onClick={() => {
              const portalUrl = meoPortalService.getPortalUrl();
              window.open(portalUrl, '_blank', 'noopener,noreferrer');
            }}
          >
            Abrir Portal MEO Novamente
          </Button>
          
          {isAdminFlag && activeSessions.length > 0 && (
            <div style={{ 
              padding: '12px 16px', 
              backgroundColor: '#f5f5f5', 
              border: '1px solid #d9d9d9',
              borderRadius: 8,
              fontSize: '12px',
              marginTop: 16
            }}>
              <strong>Sessões Ativas ({activeSessions.length}):</strong>
              <div style={{ marginTop: 4 }}>
                {activeSessions.map(session => (
                  <div key={session.session_id} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: 2
                  }}>
                    <span>{session.crm_user}</span>
                    <Button 
                      size="small" 
                      danger 
                      onClick={() => handleForceTerminate(session.crm_user)}
                      style={{ fontSize: '10px', padding: '0 4px' }}
                    >
                      Terminar
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default MEOPortalModal; 